import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MoreHorizontal, LogIn, User, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { useUserRole } from '@/hooks/useUserRole';
import { navItems, type NavItem, type RouteAccess } from '@/nav-items';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';

/**
 * Primary navigation items shown in the bottom nav bar
 * These are the most commonly used routes
 */
const PRIMARY_NAV_PATHS = ['/', '/canvas', '/documentation'];

/**
 * Routes that should be hidden from navigation menus
 * (e.g., auth callback, password reset - utility routes)
 */
const HIDDEN_ROUTES = ['/auth/callback', '/auth/reset', '/auth'];

interface MobileNavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  access: RouteAccess;
}

/**
 * Map navItems to a simpler format for the mobile nav
 * Extract icon component from JSX element
 */
function mapNavItem(item: NavItem): MobileNavItem {
  // Extract the icon component type from the JSX element
  const iconElement = item.icon as React.ReactElement;
  const IconComponent = iconElement.type as React.ComponentType<{ className?: string }>;

  return {
    to: item.to,
    label: item.title,
    icon: IconComponent,
    access: item.access,
  };
}

interface MobileBottomNavProps {
  className?: string;
}

export function MobileBottomNav({ className }: MobileBottomNavProps) {
  const location = useLocation();
  const { session, user } = useAuth();
  const { isSuperUser } = useUserRole();
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  const isAuthenticated = !!session;
  const isAdmin = isSuperUser; // Real role from DB via useUserRole (fixes P2: non-super_user must not see admin nav)
  // HEALTH: see src/hooks/useUserRole.ts note on direct client db.select (CSP/401/Drizzle browser issues). Keep usage minimal.

  /**
   * Check if user has access to a route based on access level
   */
  const hasAccess = (access: RouteAccess): boolean => {
    switch (access) {
      case 'public':
      case 'trial':
        return true;
      case 'authenticated':
        return isAuthenticated;
      case 'admin':
        return isAdmin;
      default:
        return false;
    }
  };

  /**
   * Determine if a path is currently active
   */
  const isActive = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  /**
   * Check if any route in the "More" menu is currently active
   */
  const isMoreMenuActive = useMemo(() => {
    const moreRoutes = navItems
      .filter(item => !PRIMARY_NAV_PATHS.includes(item.to) && !HIDDEN_ROUTES.includes(item.to))
      .map(item => item.to);

    return moreRoutes.some(path => isActive(path));
  }, [location.pathname]);

  /**
   * Filter and prepare navigation items
   */
  const { primaryItems, moreMenuItems } = useMemo(() => {
    // Filter out hidden routes and map to mobile format
    const visibleItems = navItems.filter(item => !HIDDEN_ROUTES.includes(item.to)).map(mapNavItem);

    // Split into primary (bottom bar) and secondary (more menu) items
    const primary = visibleItems.filter(item => PRIMARY_NAV_PATHS.includes(item.to));
    const secondary = visibleItems.filter(
      item => !PRIMARY_NAV_PATHS.includes(item.to) && hasAccess(item.access)
    );

    return {
      primaryItems: primary,
      moreMenuItems: secondary,
    };
  }, [isAuthenticated, isAdmin]);

  /**
   * Handle closing the more menu when navigating
   */
  const handleMoreItemClick = () => {
    setMoreMenuOpen(false);
  };

  /**
   * Render a single nav item
   */
  const renderNavItem = (item: MobileNavItem, active: boolean, onClick?: () => void) => {
    const Icon = item.icon;

    return (
      <Link
        key={item.to}
        to={item.to}
        onClick={onClick}
        className={cn(
          'flex flex-col items-center justify-center flex-1 h-full px-2 py-1',
          'transition-colors duration-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md',
          active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
        )}
        aria-current={active ? 'page' : undefined}
        aria-label={`Navigate to ${item.label}`}
      >
        <Icon className={cn('h-5 w-5 mb-1', active && 'text-primary')} aria-hidden="true" />
        <span className="text-xs font-medium">{item.label}</span>
      </Link>
    );
  };

  /**
   * Render the "More" button
   */
  const renderMoreButton = () => (
    <button
      onClick={() => setMoreMenuOpen(true)}
      className={cn(
        'flex flex-col items-center justify-center flex-1 h-full px-2 py-1',
        'transition-colors duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md',
        isMoreMenuActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
      )}
      aria-expanded={moreMenuOpen}
      aria-haspopup="dialog"
      aria-label="Open more navigation options"
    >
      <MoreHorizontal
        className={cn('h-5 w-5 mb-1', isMoreMenuActive && 'text-primary')}
        aria-hidden="true"
      />
      <span className="text-xs font-medium">More</span>
    </button>
  );

  /**
   * Get access level badge for more menu items
   */
  const getAccessBadge = (access: RouteAccess) => {
    if (access === 'admin') {
      return (
        <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
          Admin
        </span>
      );
    }
    return null;
  };

  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav
        className={cn(
          'fixed bottom-0 left-0 right-0 z-50 md:hidden',
          'border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80',
          'safe-area-inset-bottom',
          className
        )}
        aria-label="Mobile navigation"
        role="navigation"
      >
        <div className="flex items-center justify-around h-16 px-2">
          {/* Primary navigation items */}
          {primaryItems.map(item => renderNavItem(item, isActive(item.to)))}

          {/* More menu button */}
          {renderMoreButton()}
        </div>
      </nav>

      {/* More Menu Sheet */}
      <Sheet open={moreMenuOpen} onOpenChange={setMoreMenuOpen}>
        <SheetContent
          side="bottom"
          className="h-auto max-h-[80vh] rounded-t-2xl pb-safe"
          aria-describedby="more-menu-description"
        >
          <SheetHeader className="text-left pb-4">
            <SheetTitle className="text-lg font-semibold">Navigation</SheetTitle>
            <SheetDescription id="more-menu-description" className="text-sm text-muted-foreground">
              Access all platform features
            </SheetDescription>
          </SheetHeader>

          {/* Navigation items list */}
          <nav
            className="flex flex-col gap-1 overflow-y-auto max-h-[60vh]"
            aria-label="Additional navigation options"
          >
            {moreMenuItems.map(item => {
              const Icon = item.icon;
              const active = isActive(item.to);

              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={handleMoreItemClick}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-lg min-h-[44px]',
                    'transition-colors duration-200',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    active ? 'bg-primary/5 text-primary' : 'text-foreground hover:bg-accent'
                  )}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon
                    className={cn(
                      'h-5 w-5 flex-shrink-0',
                      active ? 'text-primary' : 'text-muted-foreground'
                    )}
                    aria-hidden="true"
                  />
                  <span className="flex-1 text-sm font-medium">
                    {item.label}
                    {getAccessBadge(item.access)}
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                </Link>
              );
            })}

            {/* Divider */}
            <div className="my-2 border-t border-border" aria-hidden="true" />

            {/* Auth/Profile link in more menu */}
            {isAuthenticated ? (
              <Link
                to="/profile"
                onClick={handleMoreItemClick}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 rounded-lg min-h-[44px]',
                  'transition-colors duration-200',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isActive('/profile')
                    ? 'bg-primary/5 text-primary'
                    : 'text-foreground hover:bg-accent'
                )}
                aria-current={isActive('/profile') ? 'page' : undefined}
              >
                <User
                  className={cn(
                    'h-5 w-5 flex-shrink-0',
                    isActive('/profile') ? 'text-primary' : 'text-muted-foreground'
                  )}
                  aria-hidden="true"
                />
                <span className="flex-1 text-sm font-medium">{user?.email || 'Profile'}</span>
                <ChevronRight className="h-4 w-4 text-stone-400" aria-hidden="true" />
              </Link>
            ) : (
              <Link
                to="/auth"
                onClick={handleMoreItemClick}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 rounded-lg min-h-[44px]',
                  'transition-colors duration-200',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  'text-foreground hover:bg-accent'
                )}
              >
                <LogIn className="h-5 w-5 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="flex-1 text-sm font-medium">Sign In</span>
                <ChevronRight className="h-4 w-4 text-stone-400" aria-hidden="true" />
              </Link>
            )}
          </nav>
        </SheetContent>
      </Sheet>
    </>
  );
}

export default MobileBottomNav;
